/**
 * Bundle 80 Task 1 - Public 14d/60d connection follow-up survey.
 * URL: /connection-followups/:token
 * Strict privacy: only the OWN row is ever surfaced.
 *
 * Bundle 80.1 Task 3 - on the thank-you screen (positive outcome only),
 * render a dedicated Yes / No / Not now card asking the participant if
 * they would like to be checked in with again at 60 days. The choice
 * is persisted via POST /api/connection-followups/:token/optin-60d.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;
const fuApi = axios.create({ baseURL: `${BASE}/api` });

const ConnectionFollowupPage = () => {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [sixtyDayChoice, setSixtyDayChoice] = useState(null);
  const [sixtyDaySaving, setSixtyDaySaving] = useState(false);

  useEffect(() => {
    let m = true;
    fuApi.get(`/connection-followups/${token}`)
      .then((r) => { if (m) setCtx(r.data); })
      .catch((e) => { if (m) toast.error(e?.response?.data?.detail || 'Invalid link'); })
      .finally(() => { if (m) setLoading(false); });
    return () => { m = false; };
  }, [token]);

  const set = (k) => (v) => setAnswers((s) => ({ ...s, [k]: typeof v === 'boolean' ? v : (v?.target?.value ?? v) }));

  const submit = async () => {
    setSubmitting(true);
    try {
      // Bundle 80.1 - the 60d opt-in is asked on the thank-you card,
      // NOT baked into the initial submit. Strip it out.
      const stripped = { ...answers };
      delete stripped.followup_60d_optin;
      const { data } = await fuApi.post(`/connection-followups/${token}/submit`, { answers: stripped });
      setDone(data);
      toast.success('Thank you.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not submit'); }
    finally { setSubmitting(false); }
  };

  const chooseSixty = async (choice) => {
    setSixtyDaySaving(true);
    try {
      const { data } = await fuApi.post(`/connection-followups/${token}/optin-60d`, { choice });
      setSixtyDayChoice(data.followup_60d_optin_state);
      if (choice === 'yes') toast.success("We'll be in touch around day 60.");
      if (choice === 'no') toast.success('Understood. No more check-ins.');
      if (choice === 'not_now') toast.success('No worries. We will not schedule anything.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save your choice');
    } finally {
      setSixtyDaySaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading...</div>;
  if (!ctx) return <div className="min-h-screen flex items-center justify-center text-neutral-500">This link is not valid.</div>;
  if (ctx.already_submitted) return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="followup-already-submitted">
      <div className="text-primary-900 font-semibold">You have already answered this follow-up. Thank you.</div>
    </div>
  );
  if (done) {
    const isPositiveDone = done.response === 'positive';
    const is14d = ctx.kind === '14d';
    const showSixtyCard = isPositiveDone && is14d && !sixtyDayChoice;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 gap-6" data-testid="followup-done">
        <div className="text-primary-900 font-semibold text-center max-w-md">
          <div className="text-2xl font-display font-bold mb-2">Thank you.</div>
          <div className="text-sm text-neutral-600">
            Your answers stay private and are used only to improve how we facilitate.
          </div>
        </div>

        {showSixtyCard ? (
          <div
            className="border border-neutral-200 rounded-xl p-5 bg-white max-w-md w-full space-y-3 shadow-sm"
            data-testid="sixty-day-optin-card"
          >
            <div className="text-[10px] uppercase tracking-widest text-secondary-800">One more question (optional)</div>
            <div className="font-display text-lg text-primary-900">
              May Dostination check in again in about two months?
            </div>
            <p className="text-sm text-neutral-600">
              We would love to hear how things settle in. One short note, then we go quiet.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => chooseSixty('yes')}
                disabled={sixtyDaySaving}
                className="bg-primary-900 text-white hover:bg-primary-800"
                data-testid="sixty-day-yes-btn"
              >
                Yes
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => chooseSixty('no')}
                disabled={sixtyDaySaving}
                data-testid="sixty-day-no-btn"
              >
                No
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => chooseSixty('not_now')}
                disabled={sixtyDaySaving}
                data-testid="sixty-day-not-now-btn"
              >
                Not now
              </Button>
            </div>
          </div>
        ) : null}

        {sixtyDayChoice === 'opted_in' ? (
          <div className="text-emerald-700 text-sm max-w-md text-center" data-testid="sixty-day-confirmation-yes">
            We will be in touch around day 60.
          </div>
        ) : null}
        {sixtyDayChoice === 'opted_out' ? (
          <div className="text-neutral-600 text-sm max-w-md text-center" data-testid="sixty-day-confirmation-no">
            Understood. We will not send any more check-ins on this connection.
          </div>
        ) : null}
        {sixtyDayChoice === 'deferred' ? (
          <div className="text-neutral-600 text-sm max-w-md text-center" data-testid="sixty-day-confirmation-not-now">
            No pressure. If you change your mind, reach out and we will schedule it.
          </div>
        ) : null}
      </div>
    );
  }

  const outcome = (answers.outcome_root || '').toLowerCase();
  const isPositive = outcome.startsWith('positive');
  const isNegative = outcome.startsWith('negative');
  // Bundle 80.1 Task 3 - the 60d opt-in is now a post-submit card.
  const positiveQuestions = (ctx.questions_positive || []).filter((q) => q.id !== 'followup_60d_optin');

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="followup-page">
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-2 text-secondary-800 text-xs uppercase tracking-widest">
          <Sparkles className="h-3.5 w-3.5" /> {ctx.kind === '14d' ? 'Two-week check-in' : 'Two-month check-in'}
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-primary-900 mt-2">
          How is your Dostination connection?
        </h1>
        <p className="text-primary-700 text-sm mt-2 max-w-xl">
          Your answers stay private between you and Dostination. The other person never sees them.
        </p>

        <div className="mt-8 space-y-3">
          {ctx.questions_root[0].options.map((o) => (
            <button
              key={o} type="button"
              onClick={() => set('outcome_root')(o)}
              className={`w-full text-left px-3 py-2 rounded-md border text-sm ${
                answers.outcome_root === o
                  ? 'bg-primary-900 text-white border-primary-900'
                  : 'bg-white text-primary-900 border-neutral-200'
              }`}
              data-testid={`followup-outcome-${o.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >{o}</button>
          ))}
        </div>

        {isPositive ? (
          <div className="mt-6 space-y-4" data-testid="followup-positive-branch">
            {positiveQuestions.map((q) => (
              <div key={q.id}>
                <label className="text-sm text-primary-900 leading-snug block mb-1">{q.label}</label>
                {q.type === 'longtext' ? (
                  <Textarea value={answers[q.id] || ''} onChange={set(q.id)} className="min-h-[80px]" data-testid={`followup-q-${q.id}`} />
                ) : q.type === 'checkbox' ? (
                  <div className="flex items-start gap-2">
                    <Checkbox checked={!!answers[q.id]} onCheckedChange={(v) => set(q.id)(!!v)} data-testid={`followup-q-${q.id}`} />
                    <span className="text-xs text-neutral-600">Check to opt in.</span>
                  </div>
                ) : q.type === 'single_select' ? (
                  <div className="flex gap-2 flex-wrap">
                    {q.options.map((o) => (
                      <button
                        key={o} type="button"
                        onClick={() => set(q.id)(o)}
                        className={`px-3 py-1.5 rounded-full text-xs border ${
                          answers[q.id] === o ? 'bg-primary-900 text-white border-primary-900'
                            : 'bg-white text-primary-800 border-neutral-200'
                        }`}
                        data-testid={`followup-q-${q.id}-${o.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      >{o}</button>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {isNegative ? (
          <div className="mt-6 space-y-4" data-testid="followup-negative-branch">
            {ctx.questions_negative.map((q) => (
              <div key={q.id}>
                <label className="text-sm text-primary-900 leading-snug block mb-1">{q.label}</label>
                {q.type === 'longtext' ? (
                  <Textarea value={answers[q.id] || ''} onChange={set(q.id)} className="min-h-[80px]" data-testid={`followup-q-${q.id}`} />
                ) : q.type === 'multi_select' ? (
                  <div className="flex gap-2 flex-wrap" data-testid={`followup-q-${q.id}`}>
                    {q.options.map((o) => {
                      const active = (answers[q.id] || []).includes(o);
                      return (
                        <button
                          key={o} type="button"
                          onClick={() => {
                            const cur = new Set(answers[q.id] || []);
                            if (active) cur.delete(o); else cur.add(o);
                            set(q.id)(Array.from(cur));
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs border ${
                            active ? 'bg-primary-900 text-white border-primary-900'
                              : 'bg-white text-primary-800 border-neutral-200'
                          }`}
                          data-testid={`followup-q-${q.id}-${o.toLowerCase().replace(/[^a-z]/g, '-')}`}
                        >{o}</button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        <div className="mt-6 flex justify-end">
          <Button onClick={submit} disabled={submitting || !answers.outcome_root}
            className="bg-primary-900 text-white hover:bg-primary-800"
            data-testid="followup-submit-btn"
          >{submitting ? 'Sending...' : 'Send'}</Button>
        </div>
      </div>
    </div>
  );
};

export default ConnectionFollowupPage;
